import { Search, X } from 'lucide-react'
import type { ComponentProps, Ref } from 'react'

import { fieldVariants } from '@/components/ui/field-variants'
import { cn } from '@/lib/utils'

interface SearchInputProps extends Omit<ComponentProps<'input'>, 'className' | 'ref'> {
  /** Required: the placeholder is not a label, and this field has no visible one. */
  'aria-label': string
  /** Rendered as a kbd on the right, e.g. "/". */
  shortcutHint?: string
  /** `false` drops the leading glass (Broker Analysis' Large holdings draws none). Default on. */
  leadingIcon?: boolean
  /**
   * Draws a ✕ at the right while the field holds text, and makes Esc clear it: the Broker Analysis
   * search fields. Called with nothing; the caller owns the value.
   */
  onClear?: () => void
  /**
   * `field-variants.ts`'s `shape`, passed straight through. Omit it: the
   * default is the one V2 search field, and `pill` now renders that too.
   * `boxed` keeps the pre-refresh hairline, as it does on Field, until its
   * caller drops the prop.
   */
  shape?: 'pill' | 'boxed'
  /** Applied to the box, which is the element that carries the visual shape. */
  className?: string
  /**
   * The `<input>`, not the box — the menus hand this to Popover and Sheet as
   * the panel's landing spot, and focus belongs in the field the member types
   * in. Declared and placed by hand rather than left to the rest spread: where
   * `...props` goes is a layout decision, and a later move of it to the box
   * would silently take the caller's ref with it.
   */
  ref?: Ref<HTMLInputElement> | undefined
}

/**
 * The search field for page filter bars (NewsFilters, and the broker,
 * portfolio and market screens' "search by …" rows) and dropdown menu panels
 * (MenuSingle, MenuMulti, AddBrokerMenu, StockPicker).
 *
 * Despite the name this file used to carry, it is not "the top-nav search":
 * TopNav renders only a button that opens Spotlight (`Search` glyph, no
 * text field), and Spotlight hand-rolls its own `<input>` for its own 640px
 * frame rather than reusing this component — grep `<SearchInput` before
 * trusting any claim about who renders this, including this comment.
 *
 * It wears the form field's own box (`fieldVariants`), so the fill and the
 * focus ring come from the one place that decision is made for every field.
 * The edge and the height are the toolbar's instead (the pages revamp,
 * 2026-10-27): from `lg` up it is 36px, level with the triggers, buttons and
 * tabs it sits beside, on the toolbar's resting `--control-border`, which
 * hover lights to `border-strong` as a trigger's does.
 *
 * Below `lg` it keeps the field's 44px and the text is `body-l` (16px) rather
 * than `body-m` (14px). The type step is not a style choice: iOS Safari zooms
 * the entire page when a focused input's computed font-size is under 16px, and
 * it does not zoom back out — so a member who taps search on a phone is left on
 * a page 1.2x too wide for the viewport, which is the horizontal-scroll bug
 * arriving by another door.
 *
 * The ring goes on the box, not the `<input>`: the box carries the shape and
 * the icon and the kbd are its flex children, so a ring drawn on the input
 * alone would stop short of the control's real edge. `field-variants.ts`
 * records why that is `has-[:focus-visible]` and not `focus-within`.
 */
export default function SearchInput({
  shape,
  shortcutHint,
  leadingIcon = true,
  onClear,
  className,
  ref,
  ...props
}: SearchInputProps) {
  const filled = props.value !== undefined && String(props.value).length > 0
  return (
    <div
      className={cn(
        fieldVariants({ shape }),
        'border-control-border hover:border-border-strong lg:h-9',
        className,
      )}
    >
      {leadingIcon ? (
        <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      ) : null}
      <input
        ref={ref}
        type="search"
        className={cn(
          'min-w-0 flex-1 bg-transparent text-body-l outline-none placeholder:text-muted-foreground-subtle lg:text-body-m',
          onClear !== undefined && '[&::-webkit-search-cancel-button]:hidden',
        )}
        {...props}
        onKeyDown={(event) => {
          props.onKeyDown?.(event)
          if (
            onClear !== undefined &&
            event.key === 'Escape' &&
            filled &&
            !event.defaultPrevented
          ) {
            event.preventDefault()
            onClear()
          }
        }}
      />
      {onClear !== undefined && filled ? (
        <button
          type="button"
          aria-label="Clear search"
          onClick={onClear}
          className="inline-flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-xs text-muted-foreground transition-colors duration-fast hover:text-foreground focus-visible:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      ) : null}
      {shortcutHint !== undefined ? (
        // The keycap `4243:76657` draws: a 20px square on the raised grey.
        <kbd className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-xs border border-border bg-secondary px-1 text-label-s text-muted-foreground">
          {shortcutHint}
        </kbd>
      ) : null}
    </div>
  )
}
